// src/content/balls.ts
//
// Phase 2 catalogue: 5 ball templates as BallDef (Omit<Ball, 'id'>).
// `id` is assigned per-instance when decks are built in Phase 4.
// Pure data — no runtime behavior. Effect logic lands in Phase 8.
//
// All effect values are sourced verbatim from
// .planning/phases/02-data-model-content-catalogues/02-CONTEXT.md.

import type { BallDef } from '../state/types';

export const BALLS: readonly BallDef[] = Object.freeze([
  {
    defId: 'standard_ball',
    name: 'Standard Ball',
    description: 'Lands on any slot with equal probability. No special effect.',
    rarity: 'common',
  },
  {
    defId: 'red_ball',
    name: 'Red Ball',
    description: 'Reds are weighted ×2 in the wheel candidate pool.',
    rarity: 'common',
    effect: { type: 'biasColor', color: 'red', weight: 2 },
  },
  {
    defId: 'magnet_ball',
    name: 'Magnet Ball',
    // Runtime resolves color = "color most fiches are placed on this spin".
    description: 'Biases ×2 toward whichever color you have the most fiches on this spin.',
    rarity: 'uncommon',
    effect: { type: 'biasMagnet', weight: 2 },
  },
  {
    defId: 'lucky_ball',
    name: 'Lucky Ball',
    description: 'If the result is 0, re-spin once for free.',
    rarity: 'uncommon',
    effect: { type: 'rerollOnZero' },
  },
  // 12b (BALL-11): loaded_ball removed from catalogue. The forceNumber /
  // doubleAllPayouts BallEffect variants remain in the union — Phase 15
  // (CONSUMABLE-06) introduces a one-shot consumable that re-uses them.
  {
    defId: 'splash_ball',
    name: 'Splash Ball',
    description: 'Scores the landing slot fully, plus 50% payout for the two adjacent wheel slots.',
    rarity: 'rare',
    effect: { type: 'splashPayout', sideRatio: 0.5 },
  },
] as const);

export const BALLS_BY_ID: Readonly<Record<string, BallDef>> = Object.freeze(
  BALLS.reduce<Record<string, BallDef>>((acc, def) => {
    acc[def.defId] = def;
    return acc;
  }, {}),
);

const BALL_SHEET_FRAME: Readonly<Record<string, number>> = {
  standard_ball: 0,
  red_ball: 1,
  magnet_ball: 2,
  lucky_ball: 3,
  splash_ball: 4,
};

export function ballSpritesheet(defId: string): { key: string; frame: number } | undefined {
  const f = BALL_SHEET_FRAME[defId];
  return f !== undefined ? { key: 'balls', frame: f } : undefined;
}
